import * as React from 'react';
import { Button } from "@/components/ui/button"; 
import { IconCopy, IconPhoto, IconFileText } from '@tabler/icons-react';
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import ImageResizeOptions from './ImageResizeOptions';

// Define the expected shape of the file prop
interface FileDetail {
  id: number | string;
  file_name: string;
  cdn_url?: string;
  content_type?: string;
  file_size?: string;
  // Add other properties from your file object as needed, e.g., dimensions, uuid
  // For example, if your DataTable schema's 'header' is the display name:
  // header: string;
}

interface FileDetailSidebarProps {
  file: FileDetail | null;
  onClose: () => void; // Function to close the sidebar
}

const FileDetailSidebar: React.FC<FileDetailSidebarProps> = ({ file, onClose }) => {
  if (!file) {
    return null;
  }

  // State for tracking current resize parameters

  const handleCopyUrl = () => {
    if (file.cdn_url) {
      navigator.clipboard.writeText(file.cdn_url)
        .then(() => {
          toast.success('URL copied to clipboard!', {
            description: 'The CDN URL has been copied to your clipboard.',
            duration: 3000
          });
        })
        .catch(err => {
          toast.error('Failed to copy URL', {
            description: 'Please try again or copy manually.',
            duration: 3000
          });
          console.error('Failed to copy URL: ', err);
        });
    }
  };

  const isImage = file.content_type?.startsWith('image/');

  // Use file.header if it's the intended display name, otherwise file.file_name
  const displayName = file.file_name; 

  return (
    <div className="bg-white text-gray-900 p-6 rounded-lg shadow-lg h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold truncate" title={displayName}>{displayName}</h3>
        <Button 
          onClick={onClose} 
          variant="ghost" 
          size="icon" 
          className="text-gray-500 hover:text-gray-800"
        >
          &times;
        </Button>
      </div>

      <div className="mb-6 aspect-video bg-gray-100 rounded flex items-center justify-center overflow-hidden border border-gray-200">
        {isImage && file.cdn_url ? (
          <img src={file.cdn_url} alt={displayName} className="max-w-full max-h-full object-contain" />
        ) : (
          <IconFileText size={48} className="text-gray-400" />
        )}
      </div>

      <div className="text-xs text-gray-500 mb-4">
        {file.content_type && <span>{file.content_type.toUpperCase()} / </span>}
        {file.file_size && <span>{file.file_size}</span>}
        {/* Add dimensions here if available, e.g., / {file.dimensions} */}
      </div>

      <div className="mb-2 font-semibold text-sm text-gray-700">CDN URL</div>
      <div className="flex items-center">
        <input 
          type="text" 
          readOnly 
          value={file.cdn_url || ''} 
          className="bg-gray-50 border border-gray-300 text-gray-700 text-xs rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 truncate"
        />
        {file.cdn_url && (
          <Button variant="ghost" size="icon" onClick={handleCopyUrl} className="ml-2 text-gray-500 hover:bg-gray-200 hover:text-gray-800 p-2.5">
            <IconCopy size={16} />
          </Button>
        )}
      </div>

      {file.content_type && file.content_type.startsWith('image/') && (
        <ImageResizeOptions imageUrl={file.cdn_url || ''} />
      )}
      
      {/* Spacer to push content up if sidebar is taller than content */}
      <div className="flex-grow"></div> 

      {/* Add more details as needed */}
    </div>
  );
};

export default FileDetailSidebar;
